import {
  AbstractControl,
  AbstractControlOptions,
  AsyncValidatorFn,
  FormArray,
  ValidatorFn,
} from '@angular/forms';

import { processNestedControls } from './form-helpers';

/**
 * CustomFormArray extends Angular's FormArray with additional functionality for dynamic control management.
 *
 * @description
 * This custom form array component supports:
 * - Dynamic control creation and removal based on data length
 * - Automatic synchronization of array controls with data using a provided factory function
 * - Custom form controls with multi-key options and value transformations
 * - Nested form structures (groups, arrays, and controls)
 *
 * Key features and edge cases:
 * 1. customPatchValue() adjust the array length to match the input data
 * 2. Each element can be any AbstractControl type (FormControl, FormGroup, CustomFormControl, etc.)
 * 3. When a controlFactory is provided, new controls are created using the factory
 * 4. Removing excess controls happens automatically when the input array is shorter
 * 5. Handles null/undefined values in the input array by passing them to the factory
 * 6. Works with deeply nested form structures through processNestedControls
 */
export class CustomFormArray<
  T extends AbstractControl<any, any, any> = any,
> extends FormArray<T> {
  private readonly _controlFactory: ((value?: any) => T) | undefined;

  /**
   * Creates a new CustomFormArray instance
   *
   * @param controls Initial array of form controls to include in this array
   * @param controlFactory Optional factory function to create new controls dynamically
   *                      The factory receives the corresponding value from the data array
   *                      If not provided, dynamic control creation/removal is disabled
   * @param validatorOrOpts Angular validators or options for this form array
   * @param asyncValidator Angular async validators for this form array
   *
   * @example
   * ```typescript
   * // Basic control factory
   * const controlFactory = (value?: any) => new FormControl(value || '');
   * const myArray = new CustomFormArray([], controlFactory);
   *
   * // Complex factory creating nested form groups
   * const complexFactory = (value?: any) => new FormGroup({
   *   id: new FormControl(value?.id || ''),
   *   name: new FormControl(value?.name || '')
   * });
   * ```
   */
  constructor(
    controls: T[],
    controlFactory?: (value?: any) => T,
    validatorOrOpts?:
      ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
  ) {
    super(controls, validatorOrOpts, asyncValidator);
    this._controlFactory = controlFactory;
  }

  /**
   * Process nested CustomFormControls
   *
   * @description
   * This implementation extends the standard patchValue behavior by:
   * 1. Synchronizing the array controls to match the input value array length
   * 2. Processing nested controls including CustomFormControls with multiKeyOptions
   * 3. Handling non-array inputs by returning early without changes
   *
   * Important edge cases:
   * - FormArray.customPatchValue() will adjust the array size to match the input length
   * - For CustomFormControls, their multiKeyOptions will be evaluated against the entire value object
   * - Null/undefined values are allowed in the value array and will be processed accordingly
   *
   * @param value The array of values to patch into the form array
   * @param options Standard Angular form options
   *
   * @example
   * ```typescript
   * // With custom form controls that have multiKeyOptions
   * array.customPatchValue([
   *   { primaryKey: 'value1' },
   *   { alternateKey: 'value2' }
   * ]);
   * // Each control will search for its matching keys in the corresponding object
   *
   * // With null or non-array value - will clean the array
   * array.customPatchValue(null); // No effect
   * array.customPatchValue('not an array'); // No effect
   * ```
   */
  customPatchValue(
    value: any[],
    options?: { onlySelf?: boolean; emitEvent?: boolean }
  ): void {
    if (!value || !Array.isArray(value)) {
      return;
    }

    // Create a shared map for tracking processed controls
    const processedControlsMap = new Map<AbstractControl, boolean>();

    processNestedControls(this, value, options, processedControlsMap);

    this.patchValue(value, options);
  }

  /**
   * Synchronizes the form controls with the value array
   * Creates new controls if needed or removes excess ones
   * This is a public method that can be used by form helper functions
   *
   * @description
   * This is the core functionality that adjusts the array size to match the input data.
   * It has the following behaviors:
   * - Only works if a controlFactory was provided in the constructor
   * - Creates new controls using the factory when the array needs to grow
   * - Passes the corresponding value to the factory when creating each control
   * - Removes excess controls from the end when the array needs to shrink
   * - Does NOT modify existing control values (customPatchValue handle that separately)
   *
   * @param value The array to synchronize with
   *
   * @example
   * ```typescript
   * // With 2 existing controls and control factory
   * array.syncControls(['a', 'b', 'c', 'd']);
   * // Result: 4 controls, first 2 maintain their values, 2 new controls created
   *
   * // With 5 existing controls and control factory
   * array.syncControls(['x', 'y']);
   * // Result: only 2 controls remain, last 3 are removed
   *
   * // Without control factory - does nothing
   * array.syncControls(['a', 'b', 'c']);
   * // Controls remain unchanged because no factory exists to create new ones
   * ```
   */
  syncControls(value: any[]): void {
    if (!this._controlFactory || !Array.isArray(value)) {
      return;
    }

    while (this.length < value.length) {
      const newControl = this._controlFactory(value[this.length]);
      this.push(newControl);
    }

    while (this.length > value.length) {
      this.removeAt(this.length - 1);
    }
  }

  /**
   * Creates a new control using the factory and adds it to the array
   *
   * @description
   * Convenience method to add a single new control to the array.
   * - Uses the control factory to create the new control
   * - Passes the provided value to the factory
   * - Throws an error if no controlFactory was provided in the constructor
   * - The control is added to the end of the array
   *
   * @param value Optional value to pass to the control factory
   * @throws Error when no control factory has been provided
   *
   * @example
   * ```typescript
   * // With control factory defined
   * array.addControl({ id: 5, name: 'New Item' });
   * // Adds a new control created by the factory with this value
   *
   * // Without control factory
   * array.addControl(); // Throws "Control factory not provided" error
   * ```
   */
  addControl(value?: any): void {
    if (!this._controlFactory) {
      throw new Error('Control factory not provided');
    }

    const control = this._controlFactory(value);
    this.push(control);
  }
}
